import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import { combineLatest, interval } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { environment } from './../../../../../environments/environment';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { PatientInviteService, getResendCooldown, formatCooldownRemaining } from './../../../../core/services/patient-invite.service';
import { PatientService } from './../../../../core/services/patient.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RENDER_BATCH } from './../../../../shared/directives/render-more.directive';

@Component({
  selector: 'app-invite-list',
  templateUrl: './invite-list.component.html',
  styleUrls: ['./invite-list.component.scss']
})
export class InviteListComponent extends BaseComponent implements OnInit, OnChanges {
  /** Rows rendered so far; the rest render as the end of the list nears the screen. */
  shown = RENDER_BATCH;
  readonly renderBatch = RENDER_BATCH;
  @Input() filterValue: string = '';
  @Input() sortBy: 'surname' | 'firstName' | 'patientNumber' = 'surname';
  @Input() sortDirection: 'asc' | 'desc' = 'asc';
  filteredInvites: any[] = [];
  invites: any[] = [];
  loading = true;
  /** Ticks every second so cooldown countdown updates in the template. */
  cooldownTick = 0;

  constructor(
    private patientInviteService: PatientInviteService,
    private patientService: PatientService,
    private router: Router,
    private snack: MatSnackBar,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
  }

  ngOnInit() {
    // List is from patients with status 'invite' or requiresValidation (patient.id). Merge with group invites
    // so each row has inviteId, nextAllowedResendAt, inviteTokenExpires, inviteToken for resend/revoke/complete.
    combineLatest([
      this.patientService.invites$,
      this.patientInviteService.invites$
    ]).pipe(
      map(([patients, groupInvites]) => {
        // null while the practice's invites are loading.
        if (groupInvites === null) return null;
        const inviteByPatientId = groupInvites.reduce((acc: any, inv: any) => {
          const pid = inv.patientId ?? inv.Patient?.id;
          if (pid) acc[pid] = inv;
          return acc;
        }, {});
        return (patients || []).map((p: any) => {
          const inv = inviteByPatientId[p.id];
          if (!inv) return p;
          return {
            ...p,
            inviteId: inv.id,
            nextAllowedResendAt: inv.nextAllowedResendAt,
            inviteTokenExpires: p.inviteTokenExpires ?? inv.inviteExpiry,
            inviteToken: p.inviteToken ?? inv.shortToken ?? inv.inviteToken
          };
        });
      }),
      takeUntil(this.destroy$)
    ).subscribe((invites) => {
      this.loading = invites === null;
      this.invites = invites || [];
      this.filteredInvites = this.mapInvites(this.invites);
      this.sortInvitesByName();
    });
    interval(1000).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cooldownTick = Date.now();
    });
  }

  getResendCooldown(invite: any) {
    return getResendCooldown(invite?.nextAllowedResendAt);
  }

  getResendCooldownTooltip(invite: any): string {
    const c = getResendCooldown(invite?.nextAllowedResendAt);
    if (c.canResend) return 'Resend this invite';
    return `Resend available in ${formatCooldownRemaining(c.secondsRemaining)}`;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['filterValue'] || changes['sortBy'] || changes['sortDirection']) {
      this.applyFilter();
    }
  }

  private mapInvites(invites: any[]): any[] {
    return invites.map((invite) => {
      // Compute isExpired
      const isExpired = new Date(invite.inviteTokenExpires) < new Date();

      // Compute isCompleted
      // Using your existing logic:
      const isCompleted =
        invite.inviteCompleted !== null
          ? invite.inviteCompleted
          : new Date(invite.updatedAt) > new Date(invite.createdAt);

      // Compute status string
      let status = 'Pending';
      if (isExpired) {
        status = 'Expired';
      } else if (isCompleted) {
        status = 'Patient Completed';
      }

      // Classes for [ngClass]
      const statusClasses = {
        expired: isExpired,
        completed: isCompleted
      };

      // Quick-add: invite sent via quick add (requiresValidation but status !== 'invite')
      const isQuickAdd = invite.requiresValidation === true && invite.status !== 'invite';

      return {
        ...invite,
        isExpired,
        isCompleted,
        status,
        statusClasses,
        isQuickAdd
      };
    });
  }

  applyFilter() {
    this.shown = RENDER_BATCH;
    if (this.filterValue.trim() === '') {
      this.filteredInvites = this.mapInvites(this.invites);
    } else {
      const filterTerms = this.filterValue.toLowerCase().split(' ');
      const filtered = this.invites.filter((patient) => {
        const icdCodesMatch = patient.icdCodes?.some((icdCode: any) =>
          filterTerms.every(
            (term) =>
              _.includes((icdCode.code || '').toLowerCase(), term) ||
              _.includes((icdCode.description || '').toLowerCase(), term)
          )
        );

        const patientMatch = filterTerms.every(
          (term) =>
            _.includes(patient.name.toLowerCase(), term) ||
            _.includes(patient.surname.toLowerCase(), term) ||
            _.includes(patient.email?.toLowerCase(), term) ||
            _.includes(patient.patientNumber?.toLowerCase(), term) ||
            _.includes(patient.diagnosis?.toLowerCase(), term)
        );

        return icdCodesMatch || patientMatch;
      });

      // Remap the invites after filtering
      this.filteredInvites = this.mapInvites(filtered);
    }

    this.sortInvitesByName();
  }

  sortInvitesByName() {
    // Invites may not have patientNumber yet; case-insensitive, trimmed sort
    const norm = (v: any) => (v == null ? '' : String(v)).trim().toLowerCase();
    const primaryKey = (inv: any) =>
      this.sortBy === 'patientNumber'
        ? norm(inv.patientNumber)
        : this.sortBy === 'firstName'
          ? norm(inv.name)
          : norm(inv.surname);
    const secondaryKey = (inv: any) =>
      this.sortBy === 'surname' ? norm(inv.name) : norm(inv.surname);
    this.filteredInvites = _.orderBy(
      this.filteredInvites,
      [primaryKey, secondaryKey],
      [this.sortDirection, this.sortDirection]
    );
  }

  /* getStatusClasses(invite: any) {
    return {
      'expired': this.isInviteExpired(invite),
      'completed': this.isInviteCompleted(invite)
    };
  }

  getStatusIcon(invite: any) {
    if (this.isInviteExpired(invite)) {
      return 'hourglass_disabled';
    } else if (this.isInviteCompleted(invite)) {
      return 'check_circle';
    }
    return 'hourglass_empty'; // Default pending status icon
  }

  getInviteStatus(invite: any) {
    if (this.isInviteExpired(invite)) {
      return 'Expired';
    } else if (this.isInviteCompleted(invite)) {
      return 'Patient Completed';
    }
    return 'Pending';
  }

  canRevoke(invite: any) {
    return !this.isInviteExpired(invite) && !this.isInviteCompleted(invite);
  }

  canResend(invite: any) {
    return !this.isInviteCompleted(invite) && this.isInviteExpired(invite);
  }

  isInviteCompleted(invite: any): boolean {
    return invite.inviteCompleted !== null ? invite.inviteCompleted : new Date(invite.updatedAt) > new Date(invite.createdAt);
  }

  isInviteExpired(invite: any): boolean {
    return new Date(invite.inviteTokenExpires) < new Date();
  } */

  completeInvite(invite: any, event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    // Prefer shortToken (short link); fallback to inviteToken for older invites
    const token = invite.shortToken ?? invite.inviteToken;
    const link = `${environment.PATIENT_BASE_URL}/patient-invite/${token}`;
    window.open(link, '_blank');

  }

  revokeInvite(invite: any, event: MouseEvent) {
    event.stopPropagation();
    const id = invite?.inviteId ?? invite?.id;
    if (!id) return;
    this.patientInviteService.revokeInvite(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Patient invite revoked successfully.');
        },
        error: (error) => {
          console.error('Failed to revoke patient invite: ', error);
          this.showToastMessage('Failed to revoke patient invite. Please try again');
        }
      });
  }

  resendInvite(invite: any, event: MouseEvent) {
    event.stopPropagation();
    const id = invite?.inviteId ?? invite?.id;
    if (!id) return;
    this.patientInviteService.resendInvite(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Patient invite resent successfully.');
          if (result?.nextAllowedResendAt && invite) {
            invite.nextAllowedResendAt = result.nextAllowedResendAt;
          }
        },
        error: (error) => {
          console.error('Failed to resend patient invite: ', error);
          const msg = error?.nextAllowedResendAt
            ? `Resend available in ${formatCooldownRemaining(getResendCooldown(error.nextAllowedResendAt).secondsRemaining)}`
            : 'Failed to resend patient invite. Please try again';
          this.showToastMessage(msg);
          if (error?.nextAllowedResendAt && invite) {
            invite.nextAllowedResendAt = error.nextAllowedResendAt;
          }
        }
      });
  }

  loadInviteDetails(invite: any) {
    if (this.patientService.isRecordingLocked()) {
      return;
    }
    // The form saves every field back, so it opens on the full record; a list row may not carry them all.
    this.patientService.getPatientById(invite.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(patient => {
        if (!patient) {
          this.showToastMessage('There was an error loading the patient file. Please try again.');
          return;
        }
        this.patientService.setCurrentPatient({ ...patient, ...invite, status: 'opd' });
        this.router.navigate(['/patients', {
          outlets: {
            patient: ['add'],
          }
        }]);
      });
  }

  navigateToPatientFile(event: MouseEvent, patientId: string) {
    event.preventDefault();
    event.stopPropagation();
    this.patientService.getPatientById(patientId)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        this.router.navigate(['/patients', {
          outlets: {
            patient: ['file', patientId],
          }
        }]);
      }, error => {
        console.error(error);
        this.showToastMessage('There was an error loading the patient file. Please try again.');
      });
  }
}
